import React, { useState } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Box,
    Typography,
    Chip,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider
} from '@mui/material';
import {
    AccountBalance as ContractIcon,
    Add as AddIcon,
    Info as InfoIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useContracts } from '../context/ContractContext';
import logger from '../../utils/logger';

interface ContractDropdownProps {
    value: string;
    onChange: (address: string, contract?: any) => void;
    label?: string;
    includeCustom?: boolean;
    filterByType?: string[];
    placeholder?: string;
    helperText?: string;
    required?: boolean;
    disabled?: boolean;
    showContractInfo?: boolean;
}

const ContractDropdown: React.FC<ContractDropdownProps> = ({
    value,
    onChange,
    label = "Contract Address",
    includeCustom = true,
    filterByType = [],
    placeholder = "Select a contract or enter custom address",
    helperText,
    required = false,
    disabled = false,
    showContractInfo = true
}) => {
    const { contracts } = useContracts();
    const [showCustomInput, setShowCustomInput] = useState(false);
    const [customAddress, setCustomAddress] = useState('');
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);
    const [selectedContractInfo, setSelectedContractInfo] = useState<any>(null);

    // Filter contracts based on type if specified
    const filteredContracts = filterByType.length > 0
        ? contracts.filter(contract =>
            filterByType.some(type =>
                contract.name.toLowerCase().includes(type.toLowerCase())
            )
        )
        : contracts;

    // Find the currently selected contract
    const selectedContract = contracts.find(c => c.address === value);

    // Check if the current value is a valid address but not in our contracts list
    const isUnknownAddress = value &&
        value.startsWith('0x') &&
        value.length === 42 &&
        !selectedContract;

    // Handle contract selection
    const handleContractSelect = (selectedValue: string) => {
        if (selectedValue === 'custom') {
            setShowCustomInput(true);
            return;
        }

        const contract = contracts.find(c => c.address === selectedValue);
        onChange(selectedValue, contract);
        logger.log('Contract selected:', { address: selectedValue, name: contract?.name });
    };

    // Handle custom address input
    const handleCustomAddressSubmit = () => {
        if (customAddress && customAddress.startsWith('0x') && customAddress.length === 42) {
            onChange(customAddress);
            setShowCustomInput(false);
            setCustomAddress('');
            logger.log('Custom address entered:', customAddress);
        }
    };

    // Copy address to clipboard
    const copyToClipboard = (address: string) => {
        navigator.clipboard.writeText(address).then(() => {
            logger.log('Address copied to clipboard:', address);
        }).catch(err => {
            logger.error('Failed to copy address:', err);
        });
    };

    // Show contract info dialog
    const handleShowContractInfo = (contract: any) => {
        setSelectedContractInfo(contract);
        setInfoDialogOpen(true);
    };

    // Render contract option with additional info
    const renderContractOption = (contract: any) => (
        <MenuItem key={contract.address} value={contract.address}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <ContractIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                        {contract.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {contract.address.substring(0, 10)}...{contract.address.substring(contract.address.length - 8)}
                    </Typography>
                </Box>
                {showContractInfo && (
                    <Tooltip title="View contract details">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleShowContractInfo(contract);
                            }}
                        >
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </MenuItem>
    );

    return (
        <>
            <FormControl fullWidth required={required} disabled={disabled}>
                <InputLabel>{label}</InputLabel>
                <Select
                    value={value || ''}
                    label={label}
                    onChange={(e) => handleContractSelect(e.target.value)}
                    displayEmpty
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 400,
                                maxWidth: 500,
                            },
                        },
                    }}
                >
                    {/* Empty option */}
                    <MenuItem value="">
                        <Typography color="text.secondary" fontStyle="italic">
                            {placeholder}
                        </Typography>
                    </MenuItem>

                    {/* Unknown address (if current value is not in contracts list) */}
                    {isUnknownAddress && (
                        <MenuItem value={value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <ContractIcon sx={{ mr: 1, color: 'warning.main' }} />
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body1" fontWeight="medium" color="warning.main">
                                        Unknown Contract
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {value.substring(0, 10)}...{value.substring(value.length - 8)}
                                    </Typography>
                                </Box>
                            </Box>
                        </MenuItem>
                    )}

                    {/* Known contracts */}
                    {filteredContracts.length > 0 && [
                        (isUnknownAddress || includeCustom) ? <Divider key="divider-before" /> : null,
                        ...filteredContracts.map(renderContractOption),
                        includeCustom ? <Divider key="divider-after" /> : null
                    ].filter(Boolean)}

                    {/* Custom address option */}
                    {includeCustom && (
                        <MenuItem value="custom">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <AddIcon sx={{ mr: 1, color: 'secondary.main' }} />
                                <Typography color="secondary.main">
                                    Enter custom address...
                                </Typography>
                            </Box>
                        </MenuItem>
                    )}
                </Select>

                {/* Helper text and selected contract info */}
                {(helperText || selectedContract || isUnknownAddress) && (
                    <Box sx={{ mt: 1 }}>
                        {helperText && (
                            <Typography variant="caption" color="text.secondary">
                                {helperText}
                            </Typography>
                        )}
                        {selectedContract && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <Chip
                                    size="small"
                                    label={selectedContract.name}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                />
                                <Tooltip title="Copy address">
                                    <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(selectedContract.address)}
                                    >
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {showContractInfo && (
                                    <Tooltip title="View contract details">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleShowContractInfo(selectedContract)}
                                        >
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                        {isUnknownAddress && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <Chip
                                    size="small"
                                    label="Unknown Contract"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                />
                                <Tooltip title="Copy address">
                                    <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(value)}
                                    >
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {value.substring(0, 10)}...{value.substring(value.length - 8)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </FormControl>

            {/* Custom Address Input Dialog */}
            <Dialog open={showCustomInput} onClose={() => setShowCustomInput(false)}>
                <DialogTitle>Enter Custom Contract Address</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        label="Contract Address"
                        placeholder="0x..."
                        fullWidth
                        value={customAddress}
                        onChange={(e) => setCustomAddress(e.target.value)}
                        helperText="Enter a valid Ethereum address (42 characters starting with 0x)"
                        error={customAddress !== '' && (!customAddress.startsWith('0x') || customAddress.length !== 42)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCustomInput(false)}>Cancel</Button>
                    <Button
                        onClick={handleCustomAddressSubmit}
                        variant="contained"
                        disabled={!customAddress.startsWith('0x') || customAddress.length !== 42}
                    >
                        Add Address
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Contract Info Dialog */}
            <Dialog
                open={infoDialogOpen}
                onClose={() => setInfoDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ContractIcon sx={{ mr: 1 }} />
                        Contract Information
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedContractInfo && (
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <ContractIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Contract Name"
                                    secondary={selectedContractInfo.name}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <CopyIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Address"
                                    secondary={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', mr: 1 }}>
                                                {selectedContractInfo.address}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={() => copyToClipboard(selectedContractInfo.address)}
                                            >
                                                <CopyIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    }
                                />
                            </ListItem>
                            {selectedContractInfo.abi && (
                                <ListItem>
                                    <ListItemIcon>
                                        <InfoIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Functions Available"
                                        secondary={`${selectedContractInfo.abi.filter((item: any) => item.type === 'function').length} functions`}
                                    />
                                </ListItem>
                            )}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInfoDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ContractDropdown;
